import { EXTERNAL_PROFILES, type ExternalProfile, type ProjectSummary } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Select } from '../../ui/form';

export function ExternalProfileFields({
  profile,
  onProfile,
  projectIds,
  onProjects,
  prefix,
}: {
  profile: ExternalProfile | '';
  onProfile: (value: ExternalProfile | '') => void;
  projectIds: string[];
  onProjects: (ids: string[]) => void;
  prefix: string;
}) {
  const projects = useQuery({
    queryKey: ['admin', 'external-projects'],
    queryFn: () => api<ProjectSummary[]>('/projects'),
  });
  return (
    <div className="col-span-full flex min-w-0 flex-col gap-4">
      <Field
        label="Внешний профиль"
        htmlFor={`${prefix}-profile`}
        hint="Определяет доступные действия. Назначенная роль может только сузить их."
      >
        <Select
          id={`${prefix}-profile`}
          value={profile}
          onChange={(e) => onProfile(e.target.value as ExternalProfile | '')}
        >
          <option value="">— выберите профиль —</option>
          {Object.entries(EXTERNAL_PROFILES).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label="Назначенные проекты"
        hint="Выберите каждый разрешённый проект. Подпроекты отдельно; пустой список закрывает доступ."
      >
        {projects.isLoading ? <Spinner /> : null}
        <ErrorBox error={projects.error} />
        <div className="max-h-56 overflow-y-auto rounded-md border border-line p-2">
          {(projects.data ?? []).map((p) => (
            <label key={p.id} className="flex min-h-11 cursor-pointer items-center gap-3 px-2 py-2 text-sm">
              <input
                type="checkbox"
                checked={projectIds.includes(p.id)}
                onChange={(e) =>
                  onProjects(
                    e.target.checked ? [...projectIds, p.id] : projectIds.filter((id) => id !== p.id),
                  )
                }
              />
              <span className="min-w-0 break-words">
                {p.code} · {p.name}
              </span>
            </label>
          ))}
          {projectIds
            .filter((id) => !(projects.data ?? []).some((p) => p.id === id))
            .map((id) => (
              <label key={id} className="flex min-h-11 items-center gap-3 px-2 text-sm">
                <input
                  type="checkbox"
                  checked
                  onChange={() => onProjects(projectIds.filter((p) => p !== id))}
                />
                <span>Недоступный проект — снимите назначение</span>
              </label>
            ))}
        </div>
      </Field>
      {profile && profile !== 'supplier' && profile !== 'subcontractor' ? (
        <p className="text-sm text-ink-2">
          Проектный кабинет работает без финансовых данных. Организация ниже — заказчик, проектировщик или
          инспекция этого участника.
        </p>
      ) : null}
    </div>
  );
}
